import React, { useCallback, useState } from 'react';
import { Box, Inscription, Preloader, SearchInput, useDebounce } from '@common-fe/common-fe';

import { CheckStatusType, LockboxCheck, LockboxCheckType } from '@/modules/employer/components/Lockbox/lockbox.types';
import { MatchingListEmptyState } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import useGetLockboxChecksQuery from '@/modules/employer/components/Lockbox/queries/useGetLockboxChecks.query';

import ChecksList from './components/ChecksList';
import useCheckCardsList from './hooks/useCheckCardsList';

const MIN_SEARCH_QUERY_LENGTH = 3;
const MANUAL_SEARCH_STATUSES = [CheckStatusType.ISSUED, CheckStatusType.REISSUE_REQUIRED];

interface Props {
  selectedCheckId?: string;
  onSelectCheck?: (checkId?: string, checkType?: LockboxCheckType, check?: LockboxCheck) => void;
}

const ChecksSearch: React.FC<Props> = ({ selectedCheckId, onSelectCheck }) => {
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebounce(searchString, 1000);
  const { isEditMode, originalLockbox } = useLockboxDetailsStore();
  const ocrCheckNumber = originalLockbox?.ocr?.details?.checkNumber;
  const ocrAmount = originalLockbox?.ocr?.details?.amount;

  const isSearchActive = debouncedSearchString.length >= MIN_SEARCH_QUERY_LENGTH;

  const { checks, isLoading } = useGetLockboxChecksQuery({
    mode: 'MANUAL',
    searchValue: debouncedSearchString,
    statuses: MANUAL_SEARCH_STATUSES,
    enabled: isSearchActive,
  });

  const { fields } = useCheckCardsList({
    checks,
    ocrCheckNumber,
    ocrAmount,
  });

  const handleSelect = useCallback((checkId?: string) => {
    const check = checks?.find((item) => item.checkId === checkId);
    const checkType = check?.checkType === LockboxCheckType.LOCKBOX || check?.lockboxId
      ? LockboxCheckType.LOCKBOX
      : LockboxCheckType.CLAIM;
    onSelectCheck?.(checkId, checkType, check);
  }, [checks, onSelectCheck]);

  return (
    <Box data-testid="checks-search">
      <Box data-testid="checks-search_header" direction="row" justify="between" align="center">
        <Inscription color="textSecondary" whiteSpace="nowrap">
          Manual search:
        </Inscription>

        <Box margin={{ left: 'spacing12' }} width={{ max: '364px', width: '100%' }}>
          <SearchInput
            testId="checks-search"
            value={searchString}
            onChange={setSearchString}
            placeholder="Search for number, full name, related claims"
            hideTip
          />
        </Box>
      </Box>

      {isSearchActive && (
        <Box margin={{ top: 'spacing8' }}>
          <ChecksList
            fields={fields}
            selectedCheckId={selectedCheckId}
            onSelectCheck={handleSelect}
            isEditMode={isEditMode}
            showWarnings
            emptyState={(
              <Box margin={{ top: 'spacing8' }}>
                {isLoading ? (
                  <Preloader testId="checks-search_preloader" />
                ) : (
                  <MatchingListEmptyState testId="lockbox-checks" title="No Search Results" />
                )}
              </Box>
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default ChecksSearch;
